import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { FeedType } from '@/types/cbs';
import { Package } from 'lucide-react';

const feedTypes: { id: FeedType; emoji: string; dayRange: string }[] = [
  { id: 'starter', emoji: '🐣', dayRange: '1-10' },
  { id: 'grower', emoji: '🐥', dayRange: '11-24' },
  { id: 'finisher', emoji: '🐔', dayRange: '25-42' },
];

interface FeedTypePickerProps {
  selectedType: FeedType;
  suggestedType: FeedType;
  onSelect: (type: FeedType) => void;
}

export const FeedTypePicker: React.FC<FeedTypePickerProps> = ({
  selectedType,
  suggestedType,
  onSelect,
}) => {
  const { t } = useTranslation();

  return (
    <Card className="p-4">
      <h3 className="text-lg font-medium mb-4 text-center flex items-center justify-center gap-2">
        <Package className="w-5 h-5" />
        {t('cbs.feed.selectType')}
      </h3>
      <div className="grid grid-cols-3 gap-3">
        {feedTypes.map((type) => (
          <button
            key={type.id}
            onClick={() => onSelect(type.id)}
            className={cn(
              'flex flex-col items-center p-4 rounded-xl border-2 transition-all relative',
              selectedType === type.id
                ? 'border-primary bg-primary/10 scale-105'
                : 'border-border hover:border-primary/50',
              type.id === suggestedType && 'ring-2 ring-warning ring-offset-2'
            )}
          >
            <span className="text-5xl mb-2">{type.emoji}</span>
            <span className="text-sm font-bold">
              {t(`cbs.dailyLog.feed.types.${type.id}`)}
            </span>
            <span className="text-xs text-muted-foreground">
              {t('cbs.batch.day')} {type.dayRange}
            </span>
            {type.id === suggestedType && (
              <span className="absolute -top-2 -right-2 bg-warning text-warning-foreground text-xs px-2 py-0.5 rounded-full">
                {t('common.symbols.star')}
              </span>
            )}
          </button>
        ))}
      </div>
    </Card>
  );
};

export default FeedTypePicker;
